// Money, vendors, crafting and trade.
import { Coin, ContainerId, GameConstants, ItemQuality, TileEntityType } from '../constants';
import { type GameContent, type Recipe } from '../content';
import { Inventory, emptyStack, isEmpty, qualityOf, sellPriceOf, type ItemStack } from './items';

// ---- money -------------------------------------------------------------------------------------------------------

export const Money = {
  copperPerSilver: 100,
  copperPerGold: 100_00,
  copperPerPlatinum: 100_00_00,
  /** Most a character can hold (a million platinum). */
  max: 100_00_00 * 1_000_000,

  of(coin: Coin): number {
    return coin === Coin.Silver ? 100 : coin === Coin.Gold ? 100_00 : coin === Coin.Platinum ? 100_00_00 : 1;
  },

  /** An amount of copper as coins: [platinum, gold, silver, copper]. */
  split(copper: number): [number, number, number, number] {
    copper = Math.max(0, copper);
    return [Math.floor(copper / 100_00_00), Math.floor(copper / 100_00) % 100, Math.floor(copper / 100) % 100, copper % 100];
  },

  /** Non-zero coins, largest first; copper 0 for nothing. */
  coins(copper: number): { coin: Coin; count: number }[] {
    const [p, g, s, c] = Money.split(copper);
    const out: { coin: Coin; count: number }[] = [];
    if (p > 0) out.push({ coin: Coin.Platinum, count: p });
    if (g > 0) out.push({ coin: Coin.Gold, count: g });
    if (s > 0) out.push({ coin: Coin.Silver, count: s });
    if (c > 0 || out.length === 0) out.push({ coin: Coin.Copper, count: c });
    return out;
  },

  letter: (coin: Coin) => (coin === Coin.Silver ? 's' : coin === Coin.Gold ? 'g' : coin === Coin.Platinum ? 'p' : 'c'),

  /** "1p 2g 3s 4c" (zero coins left out). */
  format: (copper: number) => Money.coins(copper).map(c => `${c.count}${Money.letter(c.coin)}`).join(' '),

  /** "1 gold 5 copper", for notices. */
  formatLong: (copper: number) => Money.coins(copper).map(c => `${c.count} ${Coin[c.coin].toLowerCase()}`).join(' '),

  /** Parses "150", "1g", "2s 50c", "1p 20g" (a plain number is copper); null if it isn't money. */
  parse(text: string): number | null {
    const parts = text.split(' ').filter(p => p.length > 0);
    if (parts.length === 0) return null;
    let copper = 0;
    for (const part of parts) {
      const last = part[part.length - 1].toLowerCase();
      const coin = last === 'c' ? Coin.Copper : last === 's' ? Coin.Silver : last === 'g' ? Coin.Gold : last === 'p' ? Coin.Platinum : null;
      const digits = coin === null ? part : part.slice(0, -1);
      if (!/^\+?\d+$/.test(digits)) return null;
      const n = Number(digits);
      if (n > Money.max) return null;
      copper = Math.min(Money.max, copper + n * Money.of(coin ?? Coin.Copper));
    }
    return copper;
  },
};

// ---- vendors -----------------------------------------------------------------------------------------------------

export const Vendor = {
  /** Most of one item bought in a single request (shift-click buys this many). */
  maxBuyCount: 5,
  /** What a vendor pays for a whole stack (0: it won't buy it). */
  sellValue: (s: ItemStack, c: GameContent) => (isEmpty(s) ? 0 : sellPriceOf(s.item, c) * s.count),
  /** Junk "Sell junk" would sell: sellable Junk-quality stacks. */
  isJunk: (s: ItemStack, c: GameContent) => !isEmpty(s) && qualityOf(s.item, c) === ItemQuality.Junk && sellPriceOf(s.item, c) > 0,
};

// ---- crafting ----------------------------------------------------------------------------------------------------

export const maxCraftBatch = 50;

export const allRecipes = (c: GameContent): Recipe[] => [...c.recipes.values()];

export const recipesFor = (station: TileEntityType, c: GameContent) => allRecipes(c).filter(r => r.station === station);

export const recipeName = (r: Recipe, c: GameContent) => c.item(r.output.item)?.name ?? String(r.output.item);

/** Whether the bag holds every ingredient. */
export const hasIngredients = (r: Recipe, inv: Inventory) => r.inputs.every(i => inv.countInBag(i.item) >= i.count);

/** Crafts once if possible (the output must fit, counting the space the ingredients free). Null on success, or why not. */
export function tryCraft(r: Recipe, inv: Inventory): string | null {
  if (!hasIngredients(r, inv)) return "You don't have the ingredients.";
  const sim = inv.clone();
  for (const i of r.inputs) sim.removeFromBag(i.item, i.count);
  if (sim.add(r.output.item, r.output.count) > 0) return 'Not enough room in your inventory.';
  for (const i of r.inputs) inv.removeFromBag(i.item, i.count);
  inv.add(r.output.item, r.output.count);
  return null;
}

// ---- trade -------------------------------------------------------------------------------------------------------

/** A stack offered from the bag: its slot, the item the player saw there, and how many. */
export interface TradeSlot { slot: number; item: number; count: number }

export interface TradeOffer { slots: TradeSlot[]; copper: number }

export const emptyOffer: TradeOffer = { slots: [], copper: 0 };

/** What a player sees of an open trade; accepting names the revision seen. */
export interface TradeView {
  partner: string;
  revision: number;
  mine: TradeOffer;
  mineAccepted: boolean;
  theirs: ItemStack[];
  theirCopper: number;
  theirsAccepted: boolean;
}

export const Trade = {
  maxSlots: 8,
  startRangePixels: 6 * GameConstants.tileSize,
  keepRangePixels: 10 * GameConstants.tileSize,
  requestSeconds: 30,

  with: (o: TradeOffer, s: TradeSlot): TradeOffer => ({ ...o, slots: [...o.slots.filter(x => x.slot !== s.slot), s] }),
  without: (o: TradeOffer, slot: number): TradeOffer => ({ ...o, slots: o.slots.filter(x => x.slot !== slot) }),
  withCopper: (o: TradeOffer, copper: number): TradeOffer => ({ ...o, copper }),

  /** Why an offer can't be made from this inventory and purse, or null if it can. */
  check(offer: TradeOffer, inv: Inventory, money: number): string | null {
    if (offer.slots.length > Trade.maxSlots) return `You can offer at most ${Trade.maxSlots} stacks.`;
    if (new Set(offer.slots.map(s => s.slot)).size !== offer.slots.length) return 'That stack is already offered.';
    for (const s of offer.slots) {
      const error = Trade.checkSlot(s, inv);
      if (error) return error;
    }
    if (offer.copper < 0 || offer.copper > money) return "You don't have that much money.";
    return null;
  },

  sameAs: (a: TradeOffer, b: TradeOffer) => a.copper === b.copper && a.slots.length === b.slots.length &&
    a.slots.every((s, i) => s.slot === b.slots[i].slot && s.item === b.slots[i].item && s.count === b.slots[i].count),

  /** Takes an offer's stacks out of the bag (checked first with check). */
  take(offer: TradeOffer, from: Inventory): void {
    for (const s of offer.slots) {
      const st = from.get(ContainerId.Main, s.slot);
      from.set(ContainerId.Main, s.slot, st.count > s.count ? { ...st, count: st.count - s.count } : emptyStack);
    }
  },

  /** Puts an offer's items into the other bag; false if they didn't all fit (try it on clones first). */
  put(offer: TradeOffer, to: Inventory): boolean {
    let fits = true;
    for (const s of offer.slots) fits = to.add(s.item, s.count) === 0 && fits;
    return fits;
  },

  /** Why one offered stack isn't in the bag as offered (moved, used, soulbound...), or null. */
  checkSlot(s: TradeSlot, inv: Inventory): string | null {
    const st = inv.get(ContainerId.Main, s.slot);
    if (s.slot < 0 || s.slot >= Inventory.mainSize || isEmpty(st) || st.item !== s.item || s.count < 1 || s.count > st.count)
      return "That item isn't in your bag any more.";
    if (st.bound) return `${inv.content.item(st.item)?.name ?? 'That'} is soulbound: it can't be traded.`;
    return null;
  },
};
